import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/utils";
import { Close, Search } from "@dreamhub/designsystem/icons";
import IconContainer, { IconColor } from "@/components/ui/icon-container";
import { IconButton, IconButtonSize } from "@/components/ui/icon-button";
import { isEmpty } from "lodash";

interface SearchInputProps {
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  inputClassName?: string;
  clearButtonSize?: IconButtonSize;
  clearButtonColor?: IconColor;
  hideIcon?: boolean;
  /** Focus the input on mount — use for search inside dropdowns/popovers. */
  autoFocus?: boolean;
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder,
  value,
  onChange,
  className,
  inputClassName,
  clearButtonSize,
  clearButtonColor,
  hideIcon = false,
  autoFocus = false
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Focus on mount when requested. rAF lets it win over Radix's
  // onOpenAutoFocus, which would otherwise focus the popover container.
  useEffect(() => {
    if (!autoFocus) return;
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [autoFocus]);

  const handleClearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      onChange && onChange("");
      inputRef.current.focus();
    }
  };

  return (
    <div
      className={cn(
        "max-w-full flex items-center gap-2 border-[0.5px] rounded-lg bg-surface-field-default min-h-9 max-h-9 transition-all duration-200 ease-in-out",
        isFocused
          ? "border-[1px] border-primary bg-surface-focus-selected shadow-field-focus"
          : "border-border-field-default hover:border-border-field-hover shadow-field",
        hideIcon ? "pl-3" : "pl-2",
        "pr-2 py-[6px]",
        className
      )}
      data-test="search-input-container"
    >
      {!hideIcon && (
        <IconContainer size="5" color="tertiary">
          <Search />
        </IconContainer>
      )}
      <input
        ref={inputRef}
        type="text"
        className={cn(
          "w-full min-w-0 py-2 pr-4 text-base font-normal leading-[18px] text-primary placeholder:text-tertiary bg-transparent border-transparent rounded-lg focus:outline-none focus:border-transparent",
          inputClassName
        )}
        placeholder={placeholder}
        value={value ? value : ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-test="search-input"
      />
      <IconButton
        variant="iconNoBg"
        size={clearButtonSize || "sm"}
        onClick={handleClearInput}
        iconColor={clearButtonColor}
        className={cn(
          "shrink-0 h-full flex items-center justify-center p-1 w-[24px] mr-[-4px]",
          !isEmpty(inputRef.current?.value) ? "visible" : "invisible"
        )}
      >
        <Close />
      </IconButton>
    </div>
  );
};

export default SearchInput;
